<template>
  <div class="experiment-detail-page" style="margin-top: 8px">
    <nav class="entity-path" aria-label="breadcrumb">
      <div v-if="pathLoading" class="entity-path__loading">Loading…</div>
      <div v-else-if="pathError" class="entity-path__error">Couldn’t load path</div>
      <div v-else-if="pathSegments.length" class="entity-path__inner">
        <span v-for="(seg, idx) in pathSegments" :key="`${seg.type}-${seg.id || idx}`">
          <span class="entity-path__label">{{ seg.label }}:</span>
          <router-link v-if="seg.route" :to="seg.route">{{ seg.name }}</router-link>
          <span v-else>{{ seg.name }}</span>
          <span v-if="idx < pathSegments.length - 1" class="entity-path__sep"> / </span>
        </span>
      </div>
    </nav>

    <EntityOverviewLayout
      v-if="experiment"
      :panelTabs="overviewPanels"
      defaultPanel="files"
    >
      <template #top-left>
        <ExperimentDetails
          :experiment="experiment"
          :showMetadata="false"
          @updateExperiment="updateExperimentDetails"
          @openDatasets="datasetsVisible = true"
        />
      </template>

      <template #top-right>
        <DisplayImageGallery entityType="experiment" :entityId="experiment.id" />
      </template>

      <template #bottom-left>
        <NodeMdList
          :key="experiment.id"
          assType="experiment"
          :assId="experiment.id"
        />
      </template>

      <template #panel-files>
        <ExperimentFiles
          ref="filesComp"
          :files="files"
          :folders="folders"
          :base-path="filesBasePath"
          :loadingTree="isFilesTreeLoading"
          :experimentId="experiment.id"
          :assId="experiment.id"
          :assType="'experiment'"
          :isUploading="isUploading"
          :uploadPercent="uploadPercent"
          :uploadLabel="uploadLabel"
          @addFiles="addFiles"
          @removeFile="removeFile"
        />
      </template>

      <template #panel-comments>
        <ExperimentComments
          :comments="comments"
          :users="users"
          :files="files"
          :entityId="experiment.id"
          :isUploading="isUploading"
          :uploadPercent="uploadPercent"
          :uploadLabel="uploadLabel"
          @addComment="addComment"
          @removeComment="removeComment"
          @updateComment="handleUpdateComment"
        />
      </template>

      <template #panel-instruments>
        <Instruments
          :instruments="instruments"
          :experimentId="experiment.id"
          :experimentLocationId="experiment.location_id"
          :refreshInstruments="fetchExperimentInstruments"
        />
      </template>

      <template #panel-materials>
        <ProjectMaterials
          :materials="materials"
          :id="experiment.id"
          assType="experiment"
          :refreshMaterials="fetchExperimentMaterials"
        />
      </template>
    </EntityOverviewLayout>

    <TabularDatasetsPanel
      v-if="datasetsVisible && experiment && loggedInUser?.entra_id"
      :key="`${experiment.id}:${loggedInUser.entra_id}`"
      :experiment-id="experiment.id"
      :entra-id="loggedInUser.entra_id"
      @close="datasetsVisible = false"
      @fileCommitted="fetchFiles"
    />

    <!-- Session-based file upload modal -->
    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />
  </div>
</template>

<script>
import TabularDatasetsPanel from '@/components/TabularDatasetsPanel.vue';
import ExperimentDetails from '@/components/ExperimentDetails.vue';
import NodeMdList from '@/components/NodeMdList.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import Instruments from "@/components/InstrumentsDetails.vue";
import ProjectMaterials from "@/components/ProjectMaterials.vue";
import FileUploadSession from '@/components/FileUploadSession.vue';
import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import axios from 'axios';
import { API_ENDPOINTS } from '@/services/config';
import { parseFilesTree } from '@/utils/filesTree';
import apiService from '@/services/apiService';
import { fetchNodeEdges } from '@/utils/graphEdges';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';

export default {
  components: {
    ExperimentDetails,
    NodeMdList,
    ExperimentFiles,
    ExperimentComments,
    Instruments,
    ProjectMaterials,
    FileUploadSession,
    TabularDatasetsPanel,
    EntityOverviewLayout,
    DisplayImageGallery,
  },
  data() {
    return {
      experiment: null,
      files: [],
      folders: [],
      isFilesTreeLoading: false,
      filesBasePath: '',
      comments: [],
      users: [],   
      newUserName: '',
      instruments: [],
      materials: [],
      loading: false,
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
      pathSegments: [],
      pathLoading: false,
      pathError: null,
      datasetsVisible: false,
    };
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  computed: {
    ...mapState(['loggedInUser']),
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
        { id: 'instruments', label: 'Instruments' },
        { id: 'materials', label: 'Materials' },
      ];
    },
  },

  async created() {
    await this.loadExperiment(this.$route.params.id);
  },
  watch: {
    '$route.params.id': {
      immediate: false,
      async handler(newId) {
        this.datasetsVisible = false;
        await this.loadExperiment(newId);
      }
    }
  },

  async beforeRouteUpdate(to, from, next) {
    try {
      await this.loadExperiment(to.params.id);
      next();
    } catch (e) {
      next(e);
    }
  },

  methods: {
    async loadExperiment(id) {
      try {
        this.loading = true;

        const expRes = await axios.get(API_ENDPOINTS.experiment(id), {
          params: { entra_id: this.loggedInUser?.entra_id },
        });
        this.experiment = expRes.data;

        await this.fetchPathSegments();

        await Promise.all([
          this.fetchComments(),            
          this.fetchExperimentInstruments(),
          this.fetchExperimentMaterials(),
        ]);

        await this.fetchFiles();

      } catch (error) {
        this.toast.error('Failed to load experiment.');
        console.error('loadExperiment error:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchPathSegments() {
      if (!this.experiment) {
        this.pathSegments = [];
        return;
      }

      this.pathLoading = true;
      this.pathError = null;
      try {
        const response = await apiService.get(
          API_ENDPOINTS.path,
          { experiment_id: this.experiment.id },
          this.loggedInUser?.entra_id
        );

        const items = Array.isArray(response)
          ? response
          : response?.items || response?.data?.items || [];

        let resolvedProjectId = Number(this.experiment?.project_id) || null;
        const segments = [];

        items.forEach((item) => {
          if (item?.project?.id) {
            resolvedProjectId = Number(item.project.id) || resolvedProjectId;
            segments.push({
              type: 'project',
              id: item.project.id,
              label: 'Project',
              name: item.project.name || `#${item.project.id}`,
              route: { name: 'ProjectDetailPage', params: { id: item.project.id } },
            });
            return;
          }

          if (item?.element?.id) {
            segments.push({
              type: 'structure-element',
              id: item.element.id,
              label: item.type?.name || 'Element',
              name: item.element.name || `#${item.element.id}`,
              route: {
                name: 'StructureElementDetailPage',
                params: {
                  projectId: resolvedProjectId,
                  elementId: item.element.id,
                },
              },
            });
            return;
          }

          if (item?.experiment?.id) {
            segments.push({
              type: 'experiment',
              id: item.experiment.id,
              label: 'Experiment',
              name: item.experiment.name || `#${item.experiment.id}`,
              route: null,
            });
          }
        });

        this.pathSegments = segments;
        this.pathError = null;
      } catch (error) {
        this.pathSegments = [];
        this.pathError = error;
      } finally {
        this.pathLoading = false;
      }
    },

    async fetchFiles() {
      if (!this.experiment) return;
      const experimentId = this.experiment.id;
      this.isFilesTreeLoading = true;

      try {
        const treeResponse = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'experiment',
            entity_id: experimentId,
            entra_id: this.loggedInUser?.entra_id,
          },
        });
        const { files: experimentFiles, folders, basePath } = parseFilesTree(treeResponse.data);

        const commentFilesArrays = await Promise.all(
          (this.comments || []).map(async (comment) => {
            try {
              const response = await axios.get(API_ENDPOINTS.files, {
                params: {
                  associative_type: 'comment',
                  associative_id: comment.id,
                  entra_id: this.loggedInUser?.entra_id,
                },
              });
              return response.status === 200 ? response.data : [];
            } catch (error) {
              if (error.response?.status === 404) return [];
              console.error(`Failed to fetch files for comment ${comment.id}:`, error);
              return [];
            }
          })
        );

        const filesById = new Map();
        [...experimentFiles, ...commentFilesArrays.flat()].forEach((file) => {
          if (file?.id != null) filesById.set(file.id, file);
        });
        this.files = Array.from(filesById.values());
        this.folders = folders;
        this.filesBasePath = basePath;
      } catch (error) {
        this.files = [];
        this.folders = [];
        this.toast.error('Failed to fetch files');
        console.error('Failed to fetch files:', error);
      } finally {
        this.isFilesTreeLoading = false;
      }
    },

    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'experiment',
          entityId: this.$route.params.id,
          entraId: this.loggedInUser?.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch comments');
        console.error('Failed to fetch comments:', error);
      }
    },

    async fetchExperimentInstruments() {
      try {
        const edges = await fetchNodeEdges(this.$route.params.id, this.loggedInUser?.entra_id, {
          direction: 'outgoing',
          relationType: 'uses_instrument',
        });
        this.instruments = edges.map(({ edgeId, node }) => ({ ...node, edge_id: edgeId }));
      } catch (error) {
        this.toast.error("Failed to fetch instruments.");
        console.error("Error fetching instruments:", error);
      }
    },

    async fetchExperimentMaterials() {
      try {
        const edges = await fetchNodeEdges(this.$route.params.id, this.loggedInUser?.entra_id, {
          direction: 'outgoing',
        });
        this.materials = edges
          .filter(({ node }) => node?.node_type === 'material')
          .map(({ edgeId, relationType, node }) => ({ ...node, edge_id: edgeId, relation_type: relationType }));
      } catch (error) {
        this.toast.error("Failed to fetch materials.");
        console.error("Error fetching materials:", error);
      }
    },

    async addFiles({ files, id, folderId }) {
      // Open the session-based upload modal
      this.uploadSession = {
        visible: true,
        fileType: 'experiment',
        entityId: id,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },

    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },

    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },

    handleUploadError(error) {
      console.error("Upload session error:", error);
    },

    async removeFile(fileId) {
      try {
        await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
        this.files = this.files.filter(file => file.id !== fileId);
        this.toast.success("File removed successfully!");
      } catch (error) {
        this.toast.error('Failed to remove file');
        console.error('Failed to remove file:', error);
      }
    },

    async addComment(commentText, author_id) {
      try {
        await this.discussion.addComment({
          entityType: 'experiment',
          entityId: this.$route.params.id,
          text: commentText,
          authorId: author_id,
          entraId: this.loggedInUser?.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success("Comment added successfully!");
      } catch (error) {
        this.toast.error('Failed to add comment');
        console.error('Failed to add comment:', error);
      }
    },

    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser?.id,
          entraId: this.loggedInUser?.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success("Comment removed successfully!");
      } catch (error) {
        this.toast.error('Failed to remove comment');
        console.error('Failed to remove comment:', error);
      }
    },

    async handleUpdateComment(commentId, newText, experimentId) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'experiment',
          entityId: experimentId ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser?.id,
          entraId: this.loggedInUser?.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update comment:', error);
      }
    },

    async updateExperimentDetails(updatedExperiment) {
      const experimentId = this.$route.params.id;
      try {
        const response = await apiService.updateResource(
          `${API_ENDPOINTS.experiments}/${experimentId}`,
          updatedExperiment,
          this.loggedInUser?.id,
          this.loggedInUser?.entra_id
        );
        this.experiment = response.data;
        await this.loadExperiment(experimentId); // refresh everything
        this.toast.success("Experiment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update experiment');
        console.error('Failed to update experiment:', error);
      }
    },

    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
  },
};
</script>

<style scoped lang="scss">
.entity-path {
  font-size: .95rem;
  margin: 4px 0 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f6f7fb;
  display: inline-block;
}

.entity-path__label {
  opacity: .8;
  margin-right: 4px;
}

.entity-path__sep {
  margin: 0 6px;
  opacity: .6;
}

.entity-path__loading,
.entity-path__error {
  opacity: .85;
}

</style>
